// The storm list's pure logic: classification chips, the compact fact line, and
// the slippy-tile math under the card thumbnails. In its own module (like
// mercator.ts) so the tests can import it without dragging maplibre-gl or the
// DOM into a node bundle.
//
// Two vocabularies feed the classifier, because two feeds do. NHC's forecast
// points carry compact codes ("TS", "HU", "STS"); the Esri/JTWC live feed spells
// the type out ("Typhoon", "Tropical Depression", "Low"). One classifier
// normalises both, so a card looks the same whichever ocean its storm is in.

import { MERC_MAX_LAT, mercY } from "../map/mercator.js";

/** NHC's "no data" sentinel: later-tau points carry 9999 for direction/speed. */
export const NHC_MISSING = 9999;

export interface StormChip {
  /** What the chip says: "TD", "TS", "H3", "TY", "STY", "L", … */
  short: string;
  /** Chip colour class. Hurricane-strength → crit, storm-strength → warn,
   *  everything weaker/other → none (the neutral chip border). */
  level: "crit" | "warn" | "none";
}

/**
 * Bucket a storm type (either vocabulary) + Saffir-Simpson number into a chip.
 * Unknown types fall back to a truncated neutral chip rather than guessing a
 * strength — user-facing labels are computed or honest, never fabricated.
 */
export function stormChip(typeRaw: string, ss: number | null): StormChip {
  const t = typeRaw.trim().toLowerCase();
  const cat = ss !== null && ss >= 1 && ss <= 5 ? String(ss) : "";
  if (t === "sty" || t.includes("super typhoon")) return { short: "STY", level: "crit" };
  if (t === "ty" || t.includes("typhoon")) return { short: `TY${cat}`, level: "crit" };
  if (t === "hu" || t === "mh" || t.includes("hurricane")) {
    return { short: `H${cat}`, level: "crit" };
  }
  if (t === "sts" || t.includes("subtropical storm")) return { short: "STS", level: "warn" };
  if (t === "std" || t.includes("subtropical depression")) {
    return { short: "STD", level: "none" };
  }
  if (t === "ts" || t.includes("tropical storm")) return { short: "TS", level: "warn" };
  if (t === "td" || t.includes("tropical depression")) return { short: "TD", level: "none" };
  if (t === "ptc" || t.includes("post-tropical") || t.includes("remnant")) {
    return { short: "PTC", level: "none" };
  }
  if (t === "low" || t.includes("disturbance") || t.includes("invest")) {
    return { short: "L", level: "none" };
  }
  const short = typeRaw.trim().slice(0, 3).toUpperCase();
  return { short: short === "" ? "?" : short, level: "none" };
}

const COMPASS16 = [
  "N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE",
  "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW",
];

/** Degrees true → 16-point compass ("moving WNW" reads; "moving 292°" doesn't). */
export function compass16(deg: number): string {
  const d = ((deg % 360) + 360) % 360;
  return COMPASS16[Math.round(d / 22.5) % 16];
}

/**
 * The card's compact fact line: "40 kt · moving E 18 kt". Missing pieces
 * (null, or NHC's 9999) drop out rather than printing a sentinel; a storm with
 * nothing known gets an empty line, not an invented one.
 */
export function stormFact(
  maxwindKt: number | null,
  tcdirDeg: number | null,
  tcspdKt: number | null,
): string {
  const known = (v: number | null): v is number =>
    v !== null && Number.isFinite(v) && v !== NHC_MISSING && v >= 0;
  const bits: string[] = [];
  if (known(maxwindKt)) bits.push(`${maxwindKt} kt`);
  if (known(tcdirDeg)) {
    bits.push(`moving ${compass16(tcdirDeg)}${known(tcspdKt) ? ` ${tcspdKt} kt` : ""}`);
  } else if (known(tcspdKt)) {
    bits.push(`moving ${tcspdKt} kt`);
  }
  return bits.join(" · ");
}

export interface TileAddress {
  z: number;
  x: number;
  y: number;
  /** Where the point sits WITHIN the tile, 0..1 from the top-left corner. */
  fx: number;
  fy: number;
}

/**
 * Which slippy tile contains (lat, lon) at zoom z, and where in it the point
 * falls — the whole geometry a card thumbnail needs: one tile to fetch, one
 * fractional position to mark. Longitude wraps; latitude clamps to Web
 * Mercator's edge (a storm cannot be there, but a clamp beats a NaN).
 */
export function tileAt(lat: number, lon: number, z: number): TileAddress {
  const n = 2 ** z;
  const lonW = ((((lon + 180) % 360) + 360) % 360) - 180;
  const latC = Math.max(-MERC_MAX_LAT, Math.min(MERC_MAX_LAT, lat));
  // Clamp just inside [0, n): at the exact Mercator edge y/π is 1 to within
  // float error, which would otherwise floor to tile -1 / n.
  const clamp = (v: number): number => Math.max(0, Math.min(n - 1e-9, v));
  const xt = clamp(((lonW + 180) / 360) * n);
  const yt = clamp(((1 - mercY(latC) / Math.PI) / 2) * n);
  const x = Math.floor(xt);
  const y = Math.floor(yt);
  return { z, x, y, fx: xt - x, fy: yt - y };
}

/** A storm as the advisory package describes it — the half that carries
 *  geometry, heading and speed. */
export interface AdvisoryStorm {
  name: string;
  type: string;
  maxwind: number | null;
}

/** The same storm as NHC's current-storm summary describes it, which revises
 *  far more often and carries no geometry. */
export interface LiveStorm {
  name: string;
  classification: string;
  intensityKt: number | null;
}

/**
 * Merge the two NHC products into what a card should say.
 *
 * WHY THIS EXISTS AS A FUNCTION rather than three lines at the call site: it is
 * the whole of a correctness fix, and the browser path that exercises it cannot
 * be driven in a test (it needs maplibre and two live upstreams). Here it is
 * pure, so the rules below are actually checked.
 *
 * THE RULES, and each earned by a real defect on 2026-09-02, when the MapServer
 * was 21 hours behind on Edouard and an operator reported the console as wrong:
 *
 *  1. Classification and intensity come from the LIVE summary when present. It
 *     had him a depression at 20 kt; the advisory still said tropical storm, 40.
 *  2. The NAME comes from the live summary too. NHC writes it WITH the
 *     classification — "Tropical Storm Edouard" — so a stale advisory misstates
 *     the storm in the card title, not just in the chip.
 *  3. No live entry means the advisory stands unchanged. Losing the summary must
 *     degrade to the behaviour that shipped before it existed, never to blanks.
 *  4. A live entry missing intensity keeps the advisory's. Absent is not zero —
 *     printing 0 kt would invent a calm.
 */
export function mergeCurrentStorm(
  adv: AdvisoryStorm,
  live: LiveStorm | undefined,
): AdvisoryStorm {
  if (live === undefined) return adv;
  return {
    name: live.name !== "" ? live.name : adv.name,
    type: live.classification !== "" ? live.classification : adv.type,
    maxwind: live.intensityKt ?? adv.maxwind,
  };
}
